import {
  BonusStrategy,
  BonusType,
  BonusResult,
  ConfettiConfig,
  AmountConstraints,
} from '../interfaces/bonus.interface';
import { BlueDays } from '@utils/blue-days';

/**
 * Strategy: Bono porcentual "Días Azules" (0.2%)
 * - Activo SOLO del 22/12/2025 al 30/12/2025 (inclusive)
 * - Monto mínimo: $50,000,000
 * - Aplica para cualquier plazo (sin términos bloqueados)
 * - Términos y condiciones: reutiliza la URL del bono porcentual
 */
export class DiasAzulesBonusStrategy implements BonusStrategy {
  type = BonusType.DIAS_AZULES;
  campaignSpecific = true; // Específico de campaña (BlueDays)

  constructor(public blueDays: BlueDays | null = null) {}

  validateAmount(amount: number, minAmount: number): boolean {
    return amount >= minAmount;
  }

  private isWithinActiveWindow(startAt: Date, endAt: Date, now: Date = new Date()): boolean {
    return now >= startAt && now <= endAt;
  }

  calculateBonus(amount: number, termDays: number): BonusResult {
    const terms = this.getTermsAndConditions();
    const blue = this.blueDays?.isDayBlue(true);
    const cfg = blue && blue.bonus_blue_days ? blue.bonus_blue_days : null;
    if (!cfg) {
      return {
        isValid: false,
        bonusAmount: 0,
        message: '',
        confettiType: '',
        termsAndConditionsUrl: terms.url,
        termsAndConditionsDescription: terms.description,
      };
    }
    const percentageDecimal = (cfg.percentage || 0.2) / 100;
    const minAmount: number = typeof cfg.min_amount === 'number' ? cfg.min_amount : 50000000;
    const startAt: Date = cfg.start_at ? new Date(cfg.start_at) : new Date('2025-12-22T00:00:00');
    const endAt: Date = cfg.end_at ? new Date(cfg.end_at) : new Date('2025-12-30T23:59:59');
    const label: string = cfg.label || '+0.20%';
    if (!this.isWithinActiveWindow(startAt, endAt) || !this.validateAmount(amount, minAmount)) {
      return {
        isValid: false,
        bonusAmount: 0,
        message: '',
        confettiType: '',
        termsAndConditionsUrl: terms.url,
        termsAndConditionsDescription: terms.description,
      };
    }
    const bonusAmount = Math.round(amount * percentageDecimal * (termDays / 360));
    const displayAmount = this.formatCurrency(bonusAmount);
    return {
      isValid: true,
      label,
      value: label,
      bonusAmount,
      messages: {
        desktop:
          'Invierte desde $50.000.000 COP y MejorCDT te regala un bono extra en tu cuenta bancaria, que recibirás una vez tu CDT esté activo.',
        mobile: 'Invierte desde $50.000.000 COP y recibe un bono extra una vez tu CDT esté activo',
      },
      message_simulador: `Puedes aplicar para un bono de ${displayAmount}. Continúa con <b>la apertura de tu CDT</b> para obtenerlo.`,
      confettiType: 'snow',
      displayAmount,
      bonusTag: cfg.type || 'porcentual',
      minTermDays: cfg.terms_locked,
      tagNameBonusType: cfg.tag_name || 'DIAS_AZULES_PORCENTUAL_020',
      termsAndConditionsUrl: cfg.terms_url || terms.url,
      termsAndConditionsDescription: terms.description,
    };
  }

  getTermsAndConditions(): { url: string; description: string } {
    // Reutiliza la URL del bono porcentual
    return {
      url: '',
      description:
        'Aplican los mismos términos y condiciones del bono porcentual. El bono Días Azules es un cashback del 0.2% sobre el valor de tu inversión, válido únicamente entre el 22 y el 30 de diciembre de 2025 y sujeto a un monto mínimo de $50,000,000 COP. No acumulable con otras promociones.',
    };
  }

  getConfettiConfig(): ConfettiConfig {
    return {
      type: 'snow',
      duration: 4000,
      intensity: 'medium',
    };
  }

  getAmountConstraints(): AmountConstraints {
    return {
      locked: false,
      minAmount: 50000000,
    };
  }

  private formatCurrency(amount: number): string {
    return `$${amount.toLocaleString('es-CO')}`;
  }
}
 